import React, { Component, ComponentType } from 'react';
import { connect } from 'react-redux';
import { BeatLoader } from 'react-spinners';
import { ParticipantActionViewModel } from 'stageclip-common';

import { callApi } from 'helpers/callApi/callApi';
import { SuccessResponse, FailureResponse, Response } from 'helpers/callApi/callApi.types';
import { WithApiCallConfig, WithCallApiState } from './withCallApi.types';
import styles from './withCallApi.module.scss';

type ApiCallWrapperComponentProps = {
  action: (...args: unknown[]) => Response;
  params?: { [key: string]: string } | string | number;
  openDetails?: (p: Pick<ParticipantActionViewModel, 'data' | 'action' | 'timestamp'>) => void;
};

export const withCallApi = <Payload extends any, Data extends any>(config: WithApiCallConfig<Payload>) => (
  WrappedComponent: ComponentType<{ data: Data }>,
) => {
  class ApiCallWrapperComponent extends Component<ApiCallWrapperComponentProps, WithCallApiState<Data>> {
    constructor(props: ApiCallWrapperComponentProps) {
      super(props);

      this.state = {
        loading: true,
        error: false,
        data: null,
      };
    }

    componentDidMount() {
      this.doRequest();
    }

    doRequest = () => {
      const { action, params } = this.props;

      callApi({
        action: () => (params ? action(params) : action()),
        onSuccess: this.handleFetchSuccess,
        onFailure: this.handleFetchError,
        onCatch: this.handleFetchError,
      });
    };

    handleFetchSuccess = ({ payload }: SuccessResponse<Data>) => {
      this.setState({ data: payload, loading: false });
    };

    handleFetchError = (response: FailureResponse | Error) => {
      if ('payload' in response) {
        this.setState({
          error: true,
          loading: false,
          data: response,
        });
      }

      this.setState({
        error: true,
        loading: false,
        data: response,
      });
    };

    render() {
      const { loading, error, data } = this.state;
      const { params, ...restProps } = this.props;

      if (loading) {
        return (
          <div className={styles.loaderWrapper}>
            <BeatLoader />
          </div>
        );
      }

      if (error) {
        return <div>Something went wrong. Try refresh page</div>;
      }

      return <WrappedComponent data={data as Data} {...restProps} />;
    }
  }

  const mapDispatchToProps = (dispatch: Function) => ({
    action: (...args: unknown[]) => dispatch(config.action(...args)),
  });

  return connect<any, ReturnType<typeof mapDispatchToProps>, Omit<ApiCallWrapperComponentProps, 'action'>>(
    null,
    mapDispatchToProps,
  )(ApiCallWrapperComponent);
};
